import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Modal, Form, Input, Select, InputNumber,
  Switch, message, Card, Space, Popconfirm, Row, Col
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;
const { TextArea } = Input;

const AutoRules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await api.get('/auto-rules/');
      setRules(response.data);
    } catch (error) {
      message.error('获取规则失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async (values) => {
    try {
      const ruleData = {
        ...values,
        conditions: {
          min_ctr: values.min_ctr,
          max_cpa: values.max_cpa,
          max_frequency: values.max_frequency,
          min_spend_no_conversion: values.min_spend_no_conversion,
          min_roas: values.min_roas,
        },
        action_config: {
          budget_multiplier: values.budget_multiplier,
        },
      };

      if (editingRule) {
        await api.put(`/auto-rules/${editingRule.id}`, ruleData);
        message.success('规则更新成功');
      } else {
        await api.post('/auto-rules/', ruleData);
        message.success('规则创建成功');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingRule(null);
      fetchRules();
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
    }
  };

  const handleDeleteRule = async (id) => {
    try {
      await api.delete(`/auto-rules/${id}`);
      message.success('规则删除成功');
      fetchRules();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    form.setFieldsValue({
      name: rule.name,
      rule_type: rule.rule_type,
      description: rule.description,
      min_ctr: rule.conditions?.min_ctr,
      max_cpa: rule.conditions?.max_cpa,
      max_frequency: rule.conditions?.max_frequency,
      min_spend_no_conversion: rule.conditions?.min_spend_no_conversion,
      min_roas: rule.conditions?.min_roas,
      action: rule.action,
      budget_multiplier: rule.action_config?.budget_multiplier,
      evaluation_window: rule.evaluation_window,
      cooldown_hours: rule.cooldown_hours,
      apply_to_all: rule.apply_to_all,
    });
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '规则名称',
      dataIndex: 'name',
    },
    {
      title: '类型',
      dataIndex: 'rule_type',
      render: (type) => {
        const typeMap = {
          'pause': { color: 'error', text: '自动暂停' },
          'duplicate': { color: 'success', text: '自动复制' },
        };
        const { color, text } = typeMap[type] || { color: 'default', text: type };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '启用' : '停用'}
        </Tag>
      ),
    },
    {
      title: '评估窗口',
      dataIndex: 'evaluation_window',
      render: (hours) => `${hours}小时`,
    },
    {
      title: '冷却时间',
      dataIndex: 'cooldown_hours',
      render: (hours) => `${hours}小时`,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => handleEditRule(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除规则?"
            onConfirm={() => handleDeleteRule(record.id)}
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>自动规则</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingRule(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          创建规则
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={rules}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title={editingRule ? '编辑规则' : '创建规则'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRule(null);
        }}
        onOk={() => form.submit()}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateRule}
        >
          <Form.Item
            name="name"
            label="规则名称"
            rules={[{ required: true, message: '请输入规则名称' }]}
          >
            <Input placeholder="输入规则名称" />
          </Form.Item>

          <Form.Item
            name="rule_type"
            label="规则类型"
            rules={[{ required: true, message: '请选择规则类型' }]}
          >
            <Select placeholder="选择规则类型">
              <Option value="pause">自动暂停</Option>
              <Option value="duplicate">自动复制</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={2} placeholder="规则描述" />
          </Form.Item>

          <Card title="条件设置" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="min_ctr"
                  label="最小CTR"
                >
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder="0.01"
                    step={0.001}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="max_cpa"
                  label="最大CPA"
                >
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder="50"
                    min={0}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="max_frequency"
                  label="最大频次"
                >
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder="3"
                    min={1}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="min_spend_no_conversion"
                  label="无转化最小花费"
                >
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder="20"
                    min={0}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="min_roas"
                  label="最小ROAS"
                >
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder="2.0"
                    step={0.1}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Form.Item
            name="action"
            label="执行动作"
            rules={[{ required: true, message: '请选择执行动作' }]}
          >
            <Select placeholder="选择执行动作">
              <Option value="pause_campaign">暂停系列</Option>
              <Option value="pause_adset">暂停广告组</Option>
              <Option value="duplicate_campaign">复制系列</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="budget_multiplier"
            label="预算倍数（复制时）"
          >
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="1.5"
              min={1}
              step={0.1}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="evaluation_window"
                label="评估窗口（小时）"
                initialValue={24}
              >
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cooldown_hours"
                label="冷却时间（小时）"
                initialValue={24}
              >
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="apply_to_all"
            label="应用到所有系列"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AutoRules;
